import { AddItemButton, Checkbox, makeStyles, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import { useUser } from '@paintscout/ui-business'
import type { QuoteItemSection, RenderableItem } from '@paintscout/util/builder'
import classNames from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
import type { SortEndHandler, SortStartHandler, SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc'
import type { Item } from '../ItemTable'
import OutlinedTableCard, { CHECKBOX_RADIUS, CheckedCheckboxIcon } from './OutlinedTableCard'

export interface OutlinedItemTableProps {
  items: Item[]
  selectedItems?: Item[]
  draggable?: boolean
  checkboxes?: boolean
  showAddButton?: boolean
  addButtonLabel?: React.ReactNode
  addIndex?: number
  addButtonDraggable?: boolean
  section?: QuoteItemSection
  disabled?: boolean

  /** Tier-2 band column label (e.g. "Item"). */
  title?: React.ReactNode
  /** Right-aligned tier-2 band content (e.g. a currency symbol). */
  headerRight?: React.ReactNode
  /** Hide the tier-2 band entirely (section provides its own). */
  hideHeader?: boolean
  /** Tier-1 white section strip title (e.g. "Options"). Enables the two-tier look. */
  sectionTitle?: React.ReactNode
  /** Right-aligned content in the tier-1 section strip (e.g. a Hide pill / signature). */
  sectionAction?: React.ReactNode
  /** Right-aligned helper copy in the tier-1 section strip, left of `sectionAction`. */
  sectionSubtitle?: React.ReactNode
  /** Extra class for the tier-1 section strip (forwarded to OutlinedTableCard). */
  sectionStripClassName?: string
  /** Bump the section title a step (customer-facing Options). */
  largeSectionTitle?: boolean
  /** When defined, the band + rows + footer collapse/expand under the section strip. */
  collapsed?: boolean
  /** Tier-2 band fill variant (defaults to `light`). */
  headerVariant?: 'slate' | 'light' | 'dark'
  /** Collapse the double-border frame to a single bordered card (forwarded to OutlinedTableCard). */
  singleBorder?: boolean
  /**
   * Replace the full-bleed row-to-row border with an inset divider plus breathing room above/below
   * each row (matches `WorkOrderCardNext`'s row rule) instead of a border flush with the table edges.
   */
  insetRowDividers?: boolean

  /** Renders the content of a single item row (reuse ItemColumn + ActionColumn). */
  renderRowColumns: (item: Item, index?: number) => JSX.Element | JSX.Element[]

  onItemClick?: (event: React.MouseEvent<HTMLElement>, item: Item) => void
  onItemSelection?: (event: React.ChangeEvent<HTMLElement>, selectedItems: Item[]) => void
  onReorder?: (args: { items: Item[]; addIndex?: number }) => void
  onAddClick?: (event: React.MouseEvent<HTMLElement>, insertAfterKey: string, type?: string) => void
  className?: string
}

const useStyles = makeStyles<Theme, OutlinedItemTableProps>(
  (theme) => ({
    // 16px all round with a 16px gap between the checkbox and content, so the checkbox sits at 16px
    // and the item title lines up with the "Item" header title (measured from the mock).
    row: {
      position: 'relative',
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(2),
      padding: theme.spacing(2),
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    // The react-sortable-hoc wrappers sit between the body and each `.row`, so a CSS `:last-child`
    // rule can't reach the final row — drop the trailing divider explicitly instead.
    rowNoBorder: {
      borderBottom: 'none'
    },
    // `insetRowDividers`: a standalone divider between rows instead of each row's own border, so it
    // reads as an inset rule with a little air above/below rather than one flush with the table edges.
    rowDivider: {
      padding: theme.spacing(0.5, 2)
    },
    rowDividerLine: {
      borderTop: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`
    },
    rowClickable: ({ onItemClick }) => ({
      cursor: onItemClick ? 'pointer' : 'default',
      '&:hover': {
        backgroundColor: onItemClick ? theme.paintscout.palette.brand.Neutral.P98 : undefined
      }
    }),
    checkboxCell: {
      // keep the checkbox aligned with the first line of the row content
      paddingTop: 1,
      '@media print': {
        display: 'none'
      }
    },
    // Decorative drag affordance pinned to the top-center of the row, revealed only on row hover. The
    // whole row stays draggable (react-sortable-hoc wraps it) — this is just a hint, so it's NOT a
    // drag-handle HOC. `pointer-events: none` keeps it from ever intercepting a grab or click: the row
    // beneath it receives the event, so "pick it up anywhere" holds even over the handle.
    dragHandle: {
      position: 'absolute',
      top: theme.spacing(0.5),
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      alignItems: 'center',
      color: theme.paintscout.palette.brand.Neutral.P70,
      pointerEvents: 'none',
      opacity: 0,
      transition: 'opacity 150ms ease',
      '& svg': {
        fontSize: 20
      },
      // reveal when the row is hovered
      '$row:hover &': {
        opacity: 1
      },
      '@media print': {
        display: 'none'
      }
    },
    checkbox: {
      padding: 0,
      // MUI's checked/indeterminate glyph draws its box *inset* in a 24-unit viewBox (spans 3→21, so
      // 18 of 24 units), which means the default 24px font size renders an 18px box — exactly matching
      // the unchecked div below. Shrinking this to 18 makes the checked state visibly smaller.
      '& svg': {
        fontSize: theme.typography.pxToRem(24)
      }
    },
    // Outlined (empty) unchecked box instead of the shared Checkbox's grey fill.
    emptyCheckboxIcon: {
      backgroundColor: 'transparent !important',
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P80}`,
      borderRadius: CHECKBOX_RADIUS,
      boxSizing: 'border-box'
    },
    // The row content grows to fill the row; ItemColumn already lays out title/description/price.
    content: {
      flex: 1,
      minWidth: 0
    },
    draggingHelper: {
      zIndex: 2147483002,
      background: theme.paintscout.palette.brand.Neutral.White,
      boxShadow: theme.shadows[3],
      // the sortable helper is detached from the card, so give it the row borders back
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderRadius: theme.borderRadius.md
    },
    addButton: {
      width: '100%',
      justifyContent: 'center',
      border: `1px dashed ${theme.paintscout.palette.brand.Neutral.P85} !important`,
      borderRadius: `${theme.borderRadius.lg} !important`,
      backgroundColor: `${theme.paintscout.palette.brand.Neutral.White} !important`,
      color: theme.palette.primary.main,
      fontWeight: 600,
      minHeight: 52,
      '&:hover': {
        borderColor: `${theme.paintscout.palette.brand.Blue.Blue55} !important`
      }
    }
  }),
  { name: 'OutlinedItemTable' }
)

const SortableRow: React.ComponentClass<RowProps & SortableElementProps, any> = SortableElement(Row)
const SortableBody: React.ComponentClass<BodyProps & SortableContainerProps, any> = SortableContainer(Body)

/**
 * The div/card-based analog of `ItemTable` for the new (estimateNext) quote pipeline. Same prop
 * contract as `ItemTable` (minus the table-specific `renderHeadColumns`, replaced by `title` +
 * `headerRight` header slots) so section tables (BidTableNext, …) are near-mechanical ports of
 * their table-based counterparts. Rows are outlined rows inside an `OutlinedTableCard`; drag-reorder
 * reuses `react-sortable-hoc` (element-agnostic) with the add-button index logic ported from
 * `ItemTable`.
 */
export default function OutlinedItemTable(props: OutlinedItemTableProps) {
  const {
    renderRowColumns,
    draggable,
    onReorder,
    onItemSelection,
    selectedItems = [],
    checkboxes = true,
    items,
    showAddButton,
    addButtonLabel,
    onAddClick,
    onItemClick,
    section,
    disabled = false,
    title,
    headerRight,
    hideHeader,
    sectionTitle,
    sectionAction,
    sectionSubtitle,
    sectionStripClassName,
    largeSectionTitle,
    collapsed,
    headerVariant,
    singleBorder,
    insetRowDividers,
    className
  } = props
  const classes = useStyles(props)
  const { __ } = useTranslation()

  const { isCrew, isOfficeUser } = useUser()
  const [isDragging, setDragging] = useState(false)
  const dragStart = useRef(0)

  useEffect(() => {
    // easiest way to toggle 'grabbing' cursor - https://github.com/clauderic/react-sortable-hoc/issues/328#issuecomment-524101055
    if (isDragging) {
      document.body.style.cursor = 'grabbing'
    } else {
      document.body.style.cursor = 'default'
    }
  }, [isDragging])

  function handleItemSelection(e: any, checked: boolean, item: RenderableItem) {
    e.stopPropagation()
    if (!onItemSelection) {
      return
    }
    onItemSelection(e, checked ? [...selectedItems, item] : selectedItems.filter((i) => i.key !== item.key))
  }

  function handleAllSelection(ev: any, checked: boolean) {
    return onItemSelection && onItemSelection(ev, checked ? items : [])
  }

  const handleSortStart: SortStartHandler = (_sort, _ev) => {
    dragStart.current = Date.now()
    setDragging(true)
  }

  const handleSortEnd: SortEndHandler = ({ newIndex, oldIndex }, ev) => {
    setDragging(false)
    const dragTime = Date.now() - dragStart.current
    const wasMoved = newIndex !== oldIndex

    // treat it as a regular click if it wasn't moved and was dragged for < 1 second
    if (!wasMoved && dragTime < 1000) {
      onItemClick?.(ev as any, items[oldIndex] as Item)
    }

    if (onReorder && wasMoved) {
      onReorder({ items: arrayMove(items, oldIndex, newIndex) })
    }
  }

  function cancelDrag(event: any) {
    // composedPath is standard; event.path is non-standard and absent on iOS Safari
    const path: EventTarget[] = event.composedPath ? event.composedPath() : event.path ?? []

    // Walk up from the target to the row boundary to detect clicks inside a control (checkbox,
    // action button) that shouldn't start a drag. Rows carry a `no-drag` marker on control wrappers.
    for (const el of path) {
      if (!(el instanceof Element)) continue
      if (el.getAttribute('data-outlined-row') !== null) break
      const cls = el.getAttribute('class') || ''
      if (cls.includes('no-drag') || cls.includes('SwitchBase') || cls.includes('MuiIconButton')) {
        return true
      }
    }

    // srcElement is non-standard and absent on iOS Safari — use target as fallback
    const target = event.srcElement ?? event.target
    if (!target) return false
    const className = target.className
    if (
      className?.baseVal?.includes('SvgIcon') ||
      (typeof className === 'string' && (className.includes('no-drag') || className.includes('SwitchBase')))
    ) {
      return true
    }

    return false
  }

  // No rows means nothing to select — an empty section's header carries no checkbox (ENG-1478).
  const selectAll =
    checkboxes && onItemSelection && items.length > 0
      ? {
          checked: items.length > 0 && selectedItems.length === items.length,
          indeterminate: selectedItems.length > 0 && selectedItems.length < items.length,
          onChange: handleAllSelection
        }
      : undefined

  // The add button is a detached, fully dashed-bordered box sitting below the item block (a gap
  // between them) — the default across the new item tables. New items are appended after the last.
  const addButton = showAddButton ? (
    <AddItemButton
      className={classes.addButton}
      disabled={disabled}
      onClick={(ev) => onAddClick?.(ev, items.length ? items[items.length - 1].key : null, section)}
    >
      {addButtonLabel ?? __('Add')}
    </AddItemButton>
  ) : undefined

  return (
    <OutlinedTableCard
      className={className}
      sectionTitle={sectionTitle}
      sectionAction={sectionAction}
      sectionSubtitle={sectionSubtitle}
      sectionStripClassName={sectionStripClassName}
      largeSectionTitle={largeSectionTitle}
      collapsed={collapsed}
      headerVariant={headerVariant}
      singleBorder={singleBorder}
      title={title}
      headerRight={headerRight}
      hideHeader={hideHeader}
      // With no rows, whichever header is last in the inner block sits directly on the card's own bottom
      // border, so its divider reads as a doubled line. The add button is a *detached* footer (rendered
      // outside the inner block), so it never fills that gap. Drop the trailing divider in both shapes:
      // the tier-1 strip when it's the only header, else the tier-2 band.
      hideSectionDivider={hideHeader && items.length === 0}
      hideHeaderDivider={items.length === 0}
      selectAll={selectAll}
      detachedFooter={addButton}
    >
      <SortableBody
        updateBeforeSortStart={handleSortStart}
        onSortEnd={handleSortEnd}
        helperClass={`${classes.draggingHelper} draggingHelper`}
        lockAxis={'y'}
        pressDelay={150}
        shouldCancelStart={cancelDrag}
        useWindowAsScrollContainer={true}
      >
        {items.map((item, index) => {
          const isLastRow = index === items.length - 1
          const itemIsSelected = !!selectedItems.find((i) => i.key === item.key)
          const rowDisabled = ((isOfficeUser || isCrew) && section === 'bid') || !draggable

          return (
            <React.Fragment key={item.key}>
              {insetRowDividers && index > 0 && (
                <div className={classes.rowDivider}>
                  <div className={classes.rowDividerLine} />
                </div>
              )}
              <SortableRow
                index={index}
                disabled={rowDisabled}
                classes={classes}
                onItemClick={onItemClick ? (ev) => onItemClick(ev, item as Item) : undefined}
              >
                <div
                  className={classNames(classes.row, classes.rowClickable, {
                    [classes.rowNoBorder]: isLastRow || insetRowDividers
                  })}
                  data-outlined-row
                >
                  {checkboxes && (
                    <div className={classNames(classes.checkboxCell, 'no-drag')}>
                      <Checkbox
                        className={classes.checkbox}
                        classes={{ uncheckedIcon: classes.emptyCheckboxIcon }}
                        checkedIcon={<CheckedCheckboxIcon />}
                        checked={itemIsSelected}
                        onClick={(e) => handleItemSelection(e, !itemIsSelected, item as RenderableItem)}
                      />
                    </div>
                  )}
                  {!rowDisabled && (
                    <div className={classes.dragHandle} aria-hidden>
                      <DragHandleIcon />
                    </div>
                  )}
                  <div className={classes.content}>{renderRowColumns(item as Item, index)}</div>
                </div>
              </SortableRow>
            </React.Fragment>
          )
        })}
      </SortableBody>
    </OutlinedTableCard>
  )
}

interface RowProps {
  children?: React.ReactNode
  onItemClick?: (event: React.MouseEvent<HTMLElement>) => void
  classes: Record<string, string>
}

function Row({ children, onItemClick }: RowProps) {
  return (
    <div onClick={onItemClick} role={onItemClick ? 'button' : undefined}>
      {children}
    </div>
  )
}

interface BodyProps {
  children?: React.ReactNode
}

function Body({ children }: BodyProps) {
  return <div>{children}</div>
}
